(() => {
  'use strict';
  const tag = 'kgy-collection-auto-ranks';
  if (customElements.get(tag)) return;
  const ttl = 600000;
  const pending = new Map();
  const valid = (data, id) => data && data.collectionId === id && Array.isArray(data.ids)
    && data.ids.length <= 10 && data.ids.every(value => typeof value === 'string' && /^\d+$/.test(value))
    && new Set(data.ids).size === data.ids.length;

  function loadRanks(host) {
    const id = host.dataset.collectionId;
    const url = new URL(host.dataset.collectionUrl, location.origin);
    if (url.origin !== location.origin) return Promise.reject(new Error('Invalid collection origin'));
    url.search = '';
    url.hash = '';
    url.searchParams.set('sort_by', 'best-selling');
    url.searchParams.set('section_id', 'kgy-collection-rank-data');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const key = `kgy-auto-ranks-v1:${location.origin}:${preview || 'current'}:${id}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key));
      if (cached && Date.now() - cached.time >= 0 && Date.now() - cached.time < ttl && valid(cached.data, id)) {
        return Promise.resolve(cached.data.ids);
      }
    } catch (_) { /* Private browsing or full storage must not affect shopping. */ }
    if (pending.has(key)) return pending.get(key);
    const request = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Ranking unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = html.querySelector('[data-kgy-rank-data]');
        const data = source && JSON.parse(source.textContent);
        if (!valid(data, id)) throw new Error('Invalid ranking data');
        try { sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), data })); } catch (_) {}
        return data.ids;
      } finally {
        clearTimeout(timeout);
        pending.delete(key);
      }
    })();
    pending.set(key, request);
    return request;
  }

  customElements.define(tag, class extends HTMLElement {
    connectedCallback() {
      const grid = this.querySelector('#ProductsList');
      if (!grid || !grid.querySelector('[data-kgy-auto-rank]')) return;
      this.observer = new MutationObserver(() => this.paint());
      this.observer.observe(grid, { childList: true });
      this.started = false;
      this.onLoad = () => {
        if (this.started) return;
        this.started = true;
        clearTimeout(this.loadTimer);
        const start = () => {
          if (!this.isConnected) return;
          loadRanks(this).then(ids => {
            this.ranks = new Map(ids.map((id, index) => [id, index + 1]));
            if (this.isConnected) this.paint();
          }).catch(() => { /* No invented ranks when data is unavailable. */ });
        };
        if ('requestIdleCallback' in window) this.idle = requestIdleCallback(start, { timeout: 2000 });
        else this.timer = setTimeout(start, 0);
      };
      if (document.readyState === 'complete') this.onLoad();
      else {
        window.addEventListener('load', this.onLoad, { once: true });
        // A slow third-party resource must not prevent ranks from appearing indefinitely.
        this.loadTimer = setTimeout(this.onLoad, 6000);
      }
    }
    disconnectedCallback() {
      window.removeEventListener('load', this.onLoad);
      if (this.idle !== undefined) cancelIdleCallback(this.idle);
      clearTimeout(this.timer);
      clearTimeout(this.loadTimer);
      this.observer?.disconnect();
    }
    paint() {
      if (!this.ranks) return;
      this.querySelectorAll('[data-kgy-auto-rank]').forEach(badge => {
        const rank = this.ranks.get(badge.dataset.productId);
        badge.hidden = !rank;
        badge.textContent = rank ? `売れ筋 No.${rank}` : '';
      });
    }
  });
})();
